/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";
import { toast } from 'react-toastify';
import usePageTitle from '../../hooks/usePageTitle';

import { getUserById, updateUserProfile, uploadSingleFile } from '../../api/users';
import UserHeader from "../../components/UserView/UserHeader/UserHeader";
import UserHero from "../../components/UserView/UserHero/UserHero";
import UserSummary from "../../components/UserView/UserSummary/UserSummary";
import UserSocialAction from "../../components/UserView/UserSocialAction/UserSocialAction";
import UserDetails from "../../components/UserView/UserDetails/UserDetails";
import UserBio from '../../components/UserView/UserBio/UserBio';
import UserPhotoGallery from '../../components/UserView/UserPhotoGallery/UserPhotoGallery';
import UserFilmography from '../../components/UserView/UserFilmography/UserFilmography';
import UserVideoGallery from '../../components/UserView/UserVideoGallery/UserVideoGallery';
import UserSocials from '../../components/UserView/UserSocials/UserSocials';

import Banner from '../../components/EpkView/EpkBanner/EpkBanner';
import LoginModal from '../../components/common/Modals/LoginModal';
import AnalyticsDataService from "../../api/analytics";
import NewMessageModal from '../../components/common/Modals/NewMessageModal';
import RecommendUserModal from '../../components/common/Modals/RecommendUserModal';
import UserEditNavBar from '../../components/UserView/UserEditNavBar';
import emptyBanner from '../../images/empty_banner.jpeg';
import ActionIcon from '../../components/EpkView/EpkSocialAction/ActionIcon';
import ConfirmDialog from '../../components/common/Modals/ConfirmDialog';

function ProfileViewPage() {
  const user = useSelector((state) => state.user);
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [userData, setUserData] = useState(null);
  usePageTitle(userData?.username ? `@${userData.username}` : null);
  const [draftUser, setDraftUser] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [refresh, setRefresh] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showRecommendModal, setShowRecommendModal] = useState(false);
  const [TotalReach, setTotalReach] = useState(0);
  const [pendingDeletes, setPendingDeletes] = useState([]);
    
  const [errors, setErrors] = useState({});
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationTarget, setValidationTarget] = useState(null);
  
  const heroRef = useRef(null);
  const summaryRef = useRef(null);
  const detailsRef = useRef(null);
  const bioRef = useRef(null);
  const mediaRef = useRef(null);     
  const photoOnlyRef = useRef(null); 
  const videoOnlyRef = useRef(null); 
  const filmographyRef = useRef(null);
  const socialsRef = useRef(null);

  const sectionRefs = {
    hero: heroRef,
    summary: summaryRef,
    details: detailsRef,
    bio: bioRef,
    media: mediaRef,
    filmography: filmographyRef,
    socials: socialsRef,
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    setIsEditMode(queryParams.get("edit") === "true");
  }, [location.search]);

  useEffect(() => {
    if (id) {
      getUserById(id).then((data) => {
        setUserData(data);
      });
    }
  }, [id]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  useEffect(() => {
    if(!isEditMode) return;

    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150; 
      let currentSection = 'hero';

      const sections = [
        { id: 'hero', ref: heroRef },
        { id: 'socials', ref: socialsRef },
        { id: 'summary', ref: summaryRef },
        { id: 'details', ref: detailsRef },
        { id: 'bio', ref: bioRef },
        { id: 'media', ref: mediaRef },
        { id: 'filmography', ref: filmographyRef },
      ];

      for (let i = sections.length - 1; i >= 0; i--) {
        const el = sections[i].ref.current;
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY;
          if (top <= scrollPosition) {
            currentSection = sections[i].id;
            break;
          }
        }
      }
      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isEditMode]);

  const scrollToSection = (sectionId) => {
    const ref = sectionRefs[sectionId];
    if (ref && ref.current) {
      const y = ref.current.getBoundingClientRect().top + window.pageYOffset - 120;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const scrollToPhotos = () => {
    if (photoOnlyRef.current) {
      const y = photoOnlyRef.current.getBoundingClientRect().top + window.pageYOffset - 120;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };
  const scrollToVideos = () => {
    if (videoOnlyRef.current) {
      const y = videoOnlyRef.current.getBoundingClientRect().top + window.pageYOffset - 120;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const handleClose = (modalType) => {
    if (modalType === 'login' || !user) {
      setShowLoginModal(false);
    }
  };

  const handleShow = (modalType) => {
    if (!user) {
      setShowLoginModal(true);
      return;
    }
 
    switch (modalType) {
      case 'message':
        setShowMessageModal(true);
        break;
      case 'recommend':
        setShowRecommendModal(true);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const editing = queryParams.get("edit") === "true";
    setIsEditMode(editing);
  }, [location.search]);

  useEffect(() => {
    if (isEditMode && userData && !draftUser) {
      setDraftUser(JSON.parse(JSON.stringify(userData)));
      setIsDirty(false);
    }
    if (!isEditMode && draftUser) {
      setDraftUser(null);
      setErrors({});
      setPendingDeletes([]);
      setIsDirty(false);
    }
  }, [isEditMode, userData, draftUser]);

  const activeData = isEditMode && draftUser ? draftUser : userData;

  const HeadshotImage = React.useMemo(() => {
    const headshots = activeData?.photo_albums?.headshots || [];
    const mainObject = headshots.find(h => h.isMain) || headshots[0];
    
    const img = mainObject?.image;

    if (!img || img === '') return emptyBanner;
    if (img.startsWith('http') || img.startsWith('blob:')) return img;
    return `${process.env.REACT_APP_AWS_URL}/${img}`;
  }, [activeData?.photo_albums?.headshots]);

useEffect(() => {
  if (id && user?.id !== id && activeData?.role) {
    AnalyticsDataService.trackView(id, activeData.role)
      .catch(err => {
        console.error("Analytics Error:", err.response?.data || err.message);
      });
  }
}, [id, user?.id, activeData]);

  const discardDraft = () => {
    setDraftUser(null);
    setErrors({});
    setPendingDeletes([]);
    setIsDirty(false);
    navigate(`/user/${id}`, { replace: true });
  };

  const handleDiscard = () => {
    if (isDirty) {
      setShowDiscardModal(true);
    } else {
      discardDraft();
    }
  };

  const handleMarkMediaForDeletion = (mediaKey) => {
    if (!mediaKey) return;
    // External links and local previews are never S3 keys.
    if (/^(https?|blob|data):/i.test(mediaKey)) return;
    setPendingDeletes(prev => [...prev, mediaKey]);
    setIsDirty(true);
  };

  const clearError = (field) => {
    setErrors(prev => {
      const newErrs = { ...prev };
      delete newErrs[field];
      if (['new_headshot_file', 'image_details'].includes(field)) delete newErrs.image_details;
      if (['new_reel_file', 'new_banner_file', 'reel_url', 'banner_url'].includes(field)) delete newErrs.trailerOrBanner;
      return newErrs;
    });
  };

  const handleFieldChange = (field, value) => {
    clearError(field);
    // A function value updates from the latest draft (used after slow uploads). It is dropped once the
    // draft is gone (saved or discarded) instead of resurrecting a one-field draft.
    setDraftUser((prev) => typeof value === "function"
      ? (prev ? { ...prev, [field]: value(prev[field]) } : prev)
      : { ...prev, [field]: value });
    setIsDirty(true);
  };

  const handleSaveAndExit = async () => {
    const newErrors = {};
    const hasExistingImage = draftUser.photo_albums?.headshots?.some(h => h.isMain);
    const hasNewImage = !!draftUser.new_headshot_file;

    // 1. Validation
    if (!hasExistingImage && !hasNewImage) {
      newErrors.image_details = true;
    }
    if (!draftUser.aboutMe || draftUser.aboutMe.trim() === "") newErrors.aboutMe = true;

    if (!draftUser.summary || draftUser.summary.trim() === "") {
      newErrors.summary = "required";
    } else if (draftUser.summary.length > 100) {
      newErrors.summary = "too_long";
    }    

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);

      if (newErrors.summary) {
        setValidationTarget('summary'); 
      } else if (newErrors.aboutMe) {
        setValidationTarget('bio');
      } else {
        setValidationTarget('hero');
      }
      setShowValidationModal(true);
      return;
    }

    setErrors({});
    setIsSaving(true);

    try {
      let finalDraft = { ...draftUser };

      // 2. Metadata & Relationship Cleanup
      delete finalDraft._id;
      delete finalDraft.__v;
      delete finalDraft.createdAt;
      delete finalDraft.updatedAt;

      if (finalDraft.likes) finalDraft.likes = finalDraft.likes.map(u => u._id || u);
      if (finalDraft.favourites) finalDraft.favourites = finalDraft.favourites.map(u => u._id || u);

      // 3. Object Initialization (Crucial for new users)
      if (!finalDraft.photo_albums) finalDraft.photo_albums = { headshots: [] };
      if (!finalDraft.video_gallery) {
        finalDraft.video_gallery = { reels: [], media: [], behind: [], premieres: [] };
      }

      /**
       * 4. PHOTO LOGIC
       * Handles new uploads or selecting from existing library
       */
      if (finalDraft.new_headshot_file) {
        const headshotKey = await uploadSingleFile(finalDraft.new_headshot_file, user?.token);

        const updatedHeadshots = (finalDraft.photo_albums.headshots || []).map(h => ({
          ...h,
          isMain: false
        }));

        updatedHeadshots.push({ image: headshotKey, isMain: true });

        finalDraft.photo_albums = {
          ...finalDraft.photo_albums,
          headshots: updatedHeadshots
        };
        finalDraft.picture = headshotKey;
        delete finalDraft.new_headshot_file;
      }

      if (finalDraft.new_picture_file) {
        const pictureKey = await uploadSingleFile(finalDraft.new_picture_file, user?.token);
        finalDraft.picture = pictureKey;
        delete finalDraft.new_picture_file;
      }

      /**
       * 5. REEL / VIDEO GALLERY LOGIC
       * Maps temporary file uploads into the video_gallery.reels array
       */
      if (finalDraft.new_reel_file) {
        const videoKey = await uploadSingleFile(finalDraft.new_reel_file, user?.token);
        
        let thumbKey = null;
        if (finalDraft.new_reel_thumbnail) {
          thumbKey = await uploadSingleFile(finalDraft.new_reel_thumbnail, user?.token);
        }

        const newReelEntry = {
          url: videoKey,
          thumbnail: thumbKey || "",
          title: finalDraft.reel_title || t("My Reel"),
          isMain: true
        };

        // Set existing reels to not main
        const updatedReels = (finalDraft.video_gallery.reels || []).map(r => ({
          ...r,
          isMain: false
        }));

        updatedReels.push(newReelEntry);
        finalDraft.video_gallery.reels = updatedReels;

        // Clean up temp state fields
        delete finalDraft.new_reel_file;
        delete finalDraft.new_reel_thumbnail;
        delete finalDraft.reel_url;
        delete finalDraft.reel_thumbnail;
      }

      /**
       * 6. BANNER LOGIC
       */
      if (finalDraft.new_banner_file) {
        const bannerKey = await uploadSingleFile(finalDraft.new_banner_file, user?.token);
        finalDraft.bannerImg = bannerKey;
        delete finalDraft.new_banner_file;
        delete finalDraft.new_banner_type;
      }

      // 7. API Call. The server deletes the removed media after the save, once
      // it can see which keys the saved profile no longer uses (KIN-87).
      const updatedUser = await updateUserProfile(id, { ...finalDraft, mediaToDelete: pendingDeletes, expectedUpdatedAt: draftUser.updatedAt }, user?.token);
      setPendingDeletes([]);

      // 8. State Updates & Navigation
      setUserData(updatedUser);
      setDraftUser(null);
      setIsEditMode(false);
      setIsDirty(false);
      toast.success(t("Profile saved successfully!"));
      navigate(`/user/${id}`, { replace: true });

    } catch (error) {
      console.error("Save process failed:", error);
      // 409 + stale: someone saved after this draft was loaded (KIN-125).
      if (error.response?.status === 409 && error.response?.data?.stale) {
        setShowConflictModal(true);
        return;
      }
      // pendingDeletes is kept, so the next save retries the deletes.
      toast.error(t("Error saving profile changes. Please try again."));
    } finally {
      setIsSaving(false);
    }
  };

  if (!userData) return <div className="tw-bg-[#1E0039] tw-h-screen">{t("Loading...")}</div>;

  return (
    id && (
      <div className="tw-flex tw-flex-col tw-items-center tw-w-full tw-max-w-[100vw] tw-overflow-x-hidden tw-bg-[#1E0039] tw-relative">
        
        {isEditMode && (
          <UserEditNavBar
            userRole={userData?.role} 
            activeSection={activeSection}
            onSectionClick={scrollToSection}
            onDiscard={handleDiscard}
            onSave={handleSaveAndExit}
            isSaving={isSaving}
          />
        )}

        <div
          className={`tw-w-full tw-max-w-[1280px] tw-px-4 md:tw-px-0 ${isEditMode ? 'tw-pt-[110px]' : ''}`}
          onChange={isEditMode ? () => setIsDirty(true) : undefined}
        >

        <div ref={heroRef}>
          <UserHeader 
            data={activeData} 
            setTotalReach = {setTotalReach}
          />
       
          <UserHero
            data={activeData}
            scrollToPhotos={scrollToPhotos}
            scrollToVideos={scrollToVideos}
            isEditMode={isEditMode}
            isOwner={user?.id === id}
            onEnterEdit={() => navigate(`/user/${id}?edit=true`)}
            onChange={handleFieldChange}
            clearError={clearError}
            errors={errors}
          />
          </div>

          {isEditMode && (
            <div ref={socialsRef}>
              <UserSocials
                userInfo={activeData}
                isEditMode={isEditMode}
                onChange={handleFieldChange}
              />
            </div>
          )}

          <div ref={summaryRef}>
          <UserSummary
            data={activeData}
            isEditMode={isEditMode}
            onChange={handleFieldChange}
            errors={errors}
            clearError={clearError}
            openLoginModal={() => setShowLoginModal(true)}
            openRecommendModal={() => setShowRecommendModal(true)}
            openMessageModal={() => handleShow('message')}
          />
        </div>

        <div className="tw-h-px tw-bg-[#712CB0]/20 tw-mx-4 md:tw-mx-0" />

        <div ref={detailsRef}>
          {activeData?.role === "Actor" ? (
            <UserDetails
              data={activeData}
              isEditMode={isEditMode}
              onChange={handleFieldChange}
              errors={errors}
              clearError={clearError}
            />
          ) : (
            null
          )}
        </div>

        {activeData?.role === "Actor" && <div className="tw-h-px tw-bg-[#712CB0]/20 tw-mx-4 md:tw-mx-0" />}

        <div ref={bioRef}>
          <UserBio
            data={activeData}
            isEditMode={isEditMode}
            onChange={handleFieldChange}
          />
        </div>

        <div className="tw-h-px tw-bg-[#712CB0]/20 tw-mx-4 md:tw-mx-0" />

        <div ref={mediaRef}>
          <div ref={photoOnlyRef}>
            <UserPhotoGallery 
              data={activeData} 
              isEditMode={isEditMode} 
              onChange={handleFieldChange} 
              onMarkMediaForDeletion={handleMarkMediaForDeletion}
            />
          </div>
          <div ref={videoOnlyRef}>
            <UserVideoGallery 
              data={activeData} 
              isEditMode={isEditMode} 
              onChange={handleFieldChange} 
              onMarkMediaForDeletion={handleMarkMediaForDeletion}
            />
          </div>
          <div ref={filmographyRef}>
            <UserFilmography
              profileOwnerId={activeData?._id}
              isEditMode={isEditMode}
              onChange={handleFieldChange}
            />
          </div>
          {showLoginModal && <LoginModal close={handleClose} open={handleShow} setRefresh={setRefresh}/>}
          {showMessageModal && (
            <NewMessageModal 
              open={handleShow}
              close={handleClose} 
              userId={id} 
              user={user} 
              setRefresh={setRefresh}
            />
          )}
          {showRecommendModal && (
            <RecommendUserModal 
              close={() => setShowRecommendModal(false)}
              userId={id}
              user={user}
              epkInfo={userData} 
              setRefresh={setRefresh}
            />
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={showDiscardModal}
        variant="discard"
        title={t("Discard Changes?")}
        confirmLabel={t("Discard")}
        cancelLabel={t("Keep Editing")}
        onConfirm={() => { setShowDiscardModal(false); discardDraft(); }}
        onCancel={() => setShowDiscardModal(false)}
      >
        {t("You have unsaved changes. Are you sure you want to discard them? All your progress will be lost.")}
      </ConfirmDialog>

      <ConfirmDialog
        isOpen={showConflictModal}
        variant="discard"
        title={t("This page was changed")}
        confirmLabel={t("Reload latest version")}
        cancelLabel={t("Keep Editing")}
        onConfirm={() => window.location.reload()}
        onCancel={() => setShowConflictModal(false)}
      >
        {t("Someone else, or another tab, saved changes after you started editing. Your changes were not saved. Reload to see the latest version, then make your edits again.")}
      </ConfirmDialog>

      {showValidationModal && (
        <div className="tw-fixed tw-inset-0 tw-z-[1060] tw-flex tw-items-center tw-justify-center tw-bg-[#0a0014]/90 tw-backdrop-blur-sm tw-p-4">
          <div className="tw-bg-[#280D41] tw-border tw-border-red-500/50 tw-rounded-2xl tw-p-6 md:tw-p-8 tw-max-w-md tw-w-full tw-shadow-[0_20px_50px_rgba(239,68,68,0.2)]">
            <h3 className="tw-text-white tw-text-xl md:tw-text-2xl tw-font-bold tw-mb-4 tw-font-['Plus_Jakarta_Sans']">
              {t("Missing Information")}
            </h3>
            <p className="tw-text-[#E2BDC9] tw-text-sm tw-mb-6 tw-leading-relaxed">
              {t("Please complete the following required fields before publishing your Profile:")}
            </p>

            <ul className="tw-list-disc tw-list-inside tw-text-white tw-font-bold tw-text-sm tw-mb-8 tw-space-y-2 tw-bg-[#1E0039] tw-p-4 tw-rounded-xl">
              {/* Explicitly check for truthiness to avoid 0 or undefined rendering issues */}
              {!!errors.summary && (
                <li className="tw-text-red-400">
                  {t("Summary")} <span className="tw-text-xs tw-text-[#E2BDC9] tw-font-normal tw-ml-1">{t("(Summary Section)")}</span>
                </li>
              )}

              {!!errors.summary && (
                <li className="tw-text-red-400">
                  {errors.summary === "too_long"
                    ? `${t("Summary is too long (Max 100 characters. Currently:")} ${draftUser.summary.length})`
                    : t("Summary Section is required")}
                  <span className="tw-text-xs tw-text-[#E2BDC9] tw-font-normal tw-ml-1">{t("(Summary Section)")}</span>
                </li>
              )}

              {!!errors.aboutMe && (
                <li className="tw-text-red-400">
                  {t("Biography")} <span className="tw-text-xs tw-text-[#E2BDC9] tw-font-normal tw-ml-1">{t("(Bio Section)")}</span>
                </li>
              )}

              {!!errors.image_details && (
                <li className="tw-text-red-400">
                  {t("Headshot")} <span className="tw-text-xs tw-text-[#E2BDC9] tw-font-normal tw-ml-1">{t("(Hero Section)")}</span>
                </li>
              )}

              {!!errors.trailerOrBanner && (
                <li className="tw-text-red-400">
                  {t("Reel or Banner")} <span className="tw-text-xs tw-text-[#E2BDC9] tw-font-normal tw-ml-1">{t("(Hero Section)")}</span>
                </li>
              )}
            </ul>

            <div className="tw-flex tw-justify-end">
              <button
                onClick={() => {
                  setShowValidationModal(false);
                  if (validationTarget) scrollToSection(validationTarget);
                }}
                className="tw-px-6 tw-py-2.5 tw-bg-red-500 hover:tw-bg-red-600 tw-rounded-lg tw-text-white tw-font-bold tw-text-sm tw-uppercase tw-tracking-widest tw-border-none tw-shadow-[0_0_15px_rgba(239,68,68,0.4)] tw-transition-colors tw-cursor-pointer"
              >
                {t("Review Fields")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    )
  );
}

export default ProfileViewPage;